import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import WatchlistService from '../../services/WatchlistService';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../_shared/constants/segmentConfig';
import { TradeStateName, TradeContextName } from '../../_shared/types/trade';
import WatchButton from '../../_shared/components/WatchButton';
import useAnalytics from '../../_shared/hooks/useAnalytics';

const propTypes = {
  isWatchlisted: PropTypes.bool,
  variant: PropTypes.oneOf(['fullsize', 'mini', 'fullsize_reverse']),
  isOMP: PropTypes.bool,
  isInSaleEvent: PropTypes.bool
};

const defaultProps = {
  isWatchlisted: false,
  variant: 'fullsize',
  isOMP: false,
  isInSaleEvent: false
};

const WatchListButton = ({
  auctionId,
  dateExpected,
  dealershipId,
  dealershipName,
  isAsIs,
  isWatchlisted,
  manufacturerName,
  modelName,
  pageSource,
  tradeId,
  tradeState,
  trim,
  variant,
  vin,
  year,
  setParentWatchList,
  isOMP,
  tradeFloorPrice,
  networkName,
  isInSaleEvent
}) => {
  const intl = useIntl();
  const watchlistService = WatchlistService.get();
  const tradeSection = useSelector(
    state => state.ui?.nav?.primary?.context,
    shallowEqual
  );
  const [watchList, setWatchList] = useState(isWatchlisted);
  const [watchlistToggled, setWatchlistToggled] = useState(false);
  const track = useAnalytics();

  const toggleWatchlist = () => {
    setWatchList(!watchList);
    setWatchlistToggled(true);

    if (typeof setParentWatchList === 'function') {
      setParentWatchList(!watchList);
    }
    if (tradeId && auctionId !== null && auctionId !== undefined) {
      if (isOMP) {
        track(SegmentConfig.names.vehicleWatchlisted, {
          is_as_is: isAsIs,
          is_floor_price: !!tradeFloorPrice,
          is_in_stock:
            dateExpected === intl.formatMessage({ id: 'badges.inStock' }),
          is_timed_event: isInSaleEvent,
          make_value: manufacturerName,
          model_value: modelName,
          network_type:
            networkName === SegmentConfig.networkType.open
              ? SegmentConfig.networkType.open
              : SegmentConfig.networkType.internal,
          launch_id: auctionId?.toString(),
          trim_value: trim,
          vehicle_id: tradeId?.toString(),
          vin_value: vin,
          year_value: year,
          watchlist_state_type: !watchList ? 'Watched' : 'Unwatched'
        });
      } else {
        track(
          watchList
            ? MixpanelConfig.names.removeWatchList
            : MixpanelConfig.names.watchListTrade,
          {
            trade_id: tradeId,
            auction_id: auctionId,
            vin,
            year,
            make: manufacturerName,
            model: modelName,
            trim,
            asIs: isAsIs,
            inStock:
              dateExpected === intl.formatMessage({ id: 'badges.inStock' }),
            trade_state: TradeStateName[tradeState],
            trade_state_section: TradeContextName[tradeSection],
            seller_dealer_id: dealershipId,
            seller_dealership_name: dealershipName,
            disclosures: '',
            source: pageSource
          }
        );
      }
    }
  };

  // Set watchlist status once component is mounted and prop is passed in
  useEffect(() => {
    setWatchList(isWatchlisted);
  }, [isWatchlisted]);

  // If and only if the Watch List button was clicked, send a Watch Lit API call
  useEffect(() => {
    if (watchlistToggled) {
      // Add or Remove from Watch List depending on the watched state of the trade
      if (watchList) {
        watchlistService.addToWatchlist(tradeId);
      } else {
        watchlistService.removeFromWatchlist(tradeId);
      }
      setWatchlistToggled(false);
    }
  }, [watchList, tradeId, watchlistService, watchlistToggled]);

  return (
    <WatchButton
      variant={variant}
      isWatchlisted={watchList}
      toggleWatchlist={toggleWatchlist}
    />
  );
};

WatchListButton.propTypes = propTypes;
WatchListButton.defaultProps = defaultProps;

export default WatchListButton;
